import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/min';
import type { Option } from '@/ui';
import type { FilterDef } from '@/ui/data-table';

/** The flag emoji of an ISO country ("AU" → 🇦🇺). */
function flag(iso: string): string {
  return String.fromCodePoint(...[...iso.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** A country's dial code (1 for US and CA, 61 for AU); undefined for an unknown ISO code. */
export function dialCode(iso: string | null | undefined): number | undefined {
  if (!iso) return undefined;
  if (/^\d+$/.test(iso)) return Number(iso); // older links carried the dial code itself
  try {
    return Number(getCountryCallingCode(iso.toUpperCase() as CountryCode));
  } catch {
    return undefined;
  }
}

const cache = new Map<string, Option<string>[]>();

/**
 * Every country as a picker option: "🇦🇺 Australia" with "+61" as its hint (searchable by name or
 * code), names in the admin's language; the United States first, the rest alphabetically.
 */
export function countryOptions(language: string): Option<string>[] {
  const cached = cache.get(language);
  if (cached) return cached;
  let names: Intl.DisplayNames | undefined;
  try {
    names = new Intl.DisplayNames([language, 'en'], { type: 'region' });
  } catch {
    names = undefined;
  }
  const options = getCountries()
    .map((iso) => ({ value: iso, label: `${flag(iso)} ${names?.of(iso) ?? iso}`, hint: `+${getCountryCallingCode(iso)}` }))
    .sort((a, b) => (a.value === 'US' ? -1 : b.value === 'US' ? 1 : a.label.slice(3).localeCompare(b.label.slice(3), language)));
  cache.set(language, options);
  return options;
}

/**
 * An international number typed as one run of digits ("61423345112", "+61 423 345 112"): its dial
 * code and national number, when it is a valid number with that dial code; else null.
 */
export function splitInternational(digits: string): { countryCode: number; phone: number } | null {
  const parsed = parsePhoneNumberFromString(`+${digits.replace(/\D/g, '')}`);
  if (!parsed || !parsed.isValid()) return null;
  return { countryCode: Number(parsed.countryCallingCode), phone: Number(parsed.nationalNumber) };
}

/**
 * The phone searches' country filter: a searchable country list, the United States when unset.
 * Its URL value is the ISO country ("AU"); `withDialCode` turns it into the dial code criteria use.
 */
export function countryCodeFilter(label: string, language: string, searchPlaceholder?: string): FilterDef {
  return { type: 'combobox', key: 'countryCode', label, options: countryOptions(language), defaultValue: 'US', searchPlaceholder };
}

/** Filter values with the country filter's ISO code replaced by its dial code ("AU" → "61"). */
export function withDialCode<T extends Record<string, unknown>>(values: T): T {
  const code = dialCode(values.countryCode as string | undefined);
  return { ...values, countryCode: code === undefined ? undefined : String(code) };
}

/** Country shown by a dial-code link; prefer US for the shared +1 calling code. */
export function countryForDialCode(code: number): string | undefined {
  return code === 1 ? 'US' : getCountries().find((iso) => Number(getCountryCallingCode(iso)) === code);
}
